import { Property } from "csstype";
import { IResponsiveImages } from "src/api/server/wf-detail.types";
import { ResponsiveImage } from "src/components-mui/base/responsive-image/responsive-image";
import { TImageLoading } from "src/types/image.types";
import { IQueryParams } from "src/utils/responsive-image-util";
import { HideContent, IHideContentProps } from "./hide-content";

const RESPONSIVE_IMAGE_TIER_COUNT = 4;

interface IImageSrcSetDimension {
    width: number;
    height: number;
}

function parsePixelHeight(value: string): number | null {
    const match = /^(\d+)px$/.exec(value.trim());
    return match ? Number(match[1]) : null;
}

function createFixedHeightImageSrcSet(containerHeight: string | string[], fit?: NonNullable<IQueryParams["fit"]>) {
    const heights = Array.isArray(containerHeight) ? containerHeight : [containerHeight];

    return Array.from({ length: RESPONSIVE_IMAGE_TIER_COUNT }, (_, index) => {
        const height = parsePixelHeight(heights[index] ?? heights[heights.length - 1]);
        return height ? (fit ? { h: height, fit } : { h: height }) : undefined;
    });
}

function createFixedSizeImageSrcSet(srcSetDimensions: IImageSrcSetDimension[], fit?: NonNullable<IQueryParams["fit"]>) {
    return Array.from({ length: RESPONSIVE_IMAGE_TIER_COUNT }, (_, index) => {
        const dimensions = srcSetDimensions[index];

        if (!dimensions) {
            return undefined;
        }

        return fit
            ? {
                  w: dimensions.width,
                  h: dimensions.height,
                  fit,
              }
            : {
                  w: dimensions.width,
                  h: dimensions.height,
              };
    });
}

interface IHideContentWithResponsiveImagesProps extends IHideContentProps {
    responsiveImages: IResponsiveImages;
    minWidth?: string | string[];
    objectFit?: Property.ObjectFit | Property.ObjectFit[];
    srcSetDimensions?: IImageSrcSetDimension[];
    loading?: TImageLoading;
}

export const HideContentWithResponsiveImages = ({
    responsiveImages,
    containerHeight,
    title,
    description,
    minWidth,
    objectFit = ["unset", "cover"],
    srcSetDimensions,
    loading,
    sx,
    additionalContent,
    dataTestId,
    topOffset,
}: IHideContentWithResponsiveImagesProps) => {
    if (!responsiveImages.desktopImage) {
        return null;
    }

    const desktopUrl = responsiveImages.desktopImage.url;

    let queryParamSrcSet: Array<IQueryParams | undefined> = [];

    if (srcSetDimensions) {
        queryParamSrcSet = createFixedSizeImageSrcSet(srcSetDimensions);
    } else if (containerHeight) {
        queryParamSrcSet = createFixedHeightImageSrcSet(containerHeight);
    }

    return (
        <HideContent
            containerHeight={containerHeight}
            title={title}
            description={description}
            hasBackgroundImage
            topOffset={topOffset}
            sx={sx}
            additionalContent={additionalContent}
            dataTestId={dataTestId}
            backgroundColor="whiteAlpha.800"
        >
            <ResponsiveImage
                baseUrl={desktopUrl}
                alt={responsiveImages.desktopImage.alt || ""}
                loading={loading}
                srcSet={[
                    {
                        queryParams: queryParamSrcSet[0],
                        url: responsiveImages.mobImage?.url || desktopUrl,
                    },
                    {
                        queryParams: queryParamSrcSet[1],
                        url: responsiveImages.tabImage?.url || responsiveImages.mobImage?.url || desktopUrl,
                    },
                    {
                        queryParams: queryParamSrcSet[2],
                        url: responsiveImages.desktopSmallImage?.url || desktopUrl,
                    },
                    {
                        queryParams: queryParamSrcSet[3],
                        url: desktopUrl,
                    },
                ]}
                sx={{
                    position: "absolute",
                    width: "100%",
                    minWidth,
                    height: containerHeight,
                    top: 0,
                    left: 0,
                    objectFit,
                }}
            />
        </HideContent>
    );
};
